/**
 * @file urduVoicePickers.ts
 * @brief Interpreter Urdu voice pickers (Super Admin → AI configuration →
 *        Interpreter → Advanced) that follow "The voice the other person
 *        hears". Pure, so it runs under `node --test`.
 *
 * @details
 * Auto = a note ("Auto picks the voice"); a provider = its own male / female
 * pickers ({PROVIDER}_VOICE_UR_MALE/FEMALE, backend catalog `urduVoices`); a
 * provider without Urdu voices (Deepgram) = a note. Every provider keeps its
 * own pair, so switching provider and back keeps each pick, and the backend
 * uses the pick of whichever provider speaks the turn.
 *
 * @references ./AiSettingsSimple.tsx, BACKEND/src/infrastructure/ai/catalog.mjs (URDU_VOICES)
 * @author DES (prepared with Claude)
 * @date 16-10-2026
 */

type Option = { value: string; label: string };
type CatalogLike = {
  TTS: Record<string, { configured: boolean; urduVoices?: { male: Option[]; female: Option[] } } | undefined>;
};

/** A row in the Advanced list: a picker, or a line of text when `note` is set. */
export type VoiceStep = { key: string; label: string; hint?: string; options: Option[]; note?: string };

const PROVIDER_NAMES: Record<string, string> = { azure: "Azure", google: "Google", openai: "OpenAI", soniox: "Soniox", deepgram: "Deepgram" };
const nameOf = (p: string) => PROVIDER_NAMES[p] ?? p;
export const urduVoiceKey = (p: string, g: "male" | "female") => `${p.toUpperCase()}_VOICE_UR_${g.toUpperCase()}`;

/** Both Urdu voice pickers for one provider, or none if it has no Urdu voices. */
export function urduVoiceSteps(p: string, catalog: CatalogLike, withProviderName = false): VoiceStep[] {
  const voices = catalog.TTS[p]?.urduVoices;
  if (!voices) return [];
  return (["male", "female"] as const).map((g) => ({
    key: urduVoiceKey(p, g),
    label: `Urdu voice for a ${g} speaker${withProviderName ? ` (${nameOf(p)})` : ""}`,
    hint: `${nameOf(p)} voices that speak Urdu. Also used when ${nameOf(p)} speaks as the backup.`,
    options: voices[g],
  }));
}

/** Every configured provider's Urdu voice pickers (for the change bar, whatever is shown). */
export function allUrduVoiceSteps(catalog: CatalogLike): VoiceStep[] {
  return Object.keys(catalog.TTS).flatMap((p) => (catalog.TTS[p]?.configured ? urduVoiceSteps(p, catalog, true) : []));
}

/** What sits under the voice dropdown for the picked provider. */
export function urduVoiceBlock(ttsPick: string, catalog: CatalogLike): VoiceStep[] {
  if (!ttsPick || ttsPick === "auto") {
    return [{ key: "__urdu_voice_note", label: "Urdu voice", note: "Auto picks the voice", options: [] }];
  }
  const steps = urduVoiceSteps(ttsPick, catalog);
  if (steps.length) return steps;
  return [{ key: "__urdu_voice_note", label: "Urdu voice", note: `${nameOf(ttsPick)} has no Urdu voice; Urdu turns use the next voice`, options: [] }];
}
